import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { MapContainer } from '../../components/map'
import { LocaleCard } from '../../components/locali'
import { Button, Skeleton } from '../../components/common'
import { useLocaliStore } from '../../store'

/**
 * Homepage del sito
 * Include hero, mappa interattiva, locali in evidenza e sezione club
 */
export default function Home() {
  // Store per locali
  const { localiMappa, localiInEvidenza, fetchLocaliMappa, fetchLocaliInEvidenza } = useLocaliStore()

  // Stato per il locale selezionato sulla mappa
  const [selectedLocaleId, setSelectedLocaleId] = useState<number | undefined>()
  const [loading, setLoading] = useState(true)

  // Carica dati all'avvio
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([
        fetchLocaliMappa(),
        fetchLocaliInEvidenza(3)
      ])
      setLoading(false)
    }
    loadData()
  }, [fetchLocaliMappa, fetchLocaliInEvidenza])

  return (
    <div className="space-y-16 md:space-y-24">
      {/* Hero Section */}
      <section className="relative -mt-8 pt-8">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto py-12 md:py-20">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/30 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-gold rounded-full animate-pulse"></span>
              <span className="text-sm text-gold">Club 4x4 Conero Garage</span>
            </div>

            {/* Titolo */}
            <h1 className="font-display text-5xl md:text-7xl text-white mb-4">
              CONERO GARAGE
              <br />
              <span className="text-gold">APPROVED</span>
            </h1>

            {/* Payoff */}
            <p className="text-2xl md:text-3xl text-gold font-display mb-6">
              "Si Mangia Bene!"
            </p>

            {/* Descrizione */}
            <p className="text-lg text-gray-400 mb-8 max-w-xl mx-auto">
              La guida ai migliori ristoranti, trattorie e agriturismi delle Marche,
              scoperti e approvati dagli appassionati del club 4x4 Conero Garage.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/locali">
                <Button variant="primary" size="lg">
                  Scopri i locali
                </Button>
              </Link>
              <Link to="/come-funziona">
                <Button variant="secondary" size="lg">
                  Come funziona
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mappa interattiva */}
      <section className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-2">
            Esplora la Mappa
          </h2>
          <p className="text-gray-400">
            {localiMappa.length} locali approvati nelle Marche
          </p>
        </div>

        {loading ? (
          <Skeleton className="h-[500px] rounded-xl" />
        ) : (
          <MapContainer
            locali={localiMappa}
            selectedLocaleId={selectedLocaleId}
            onLocaleClick={(locale) => setSelectedLocaleId(locale.id)}
            height="500px"
            className="shadow-2xl"
          />
        )}
      </section>

      {/* Locali in evidenza */}
      <section className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-display text-3xl md:text-4xl text-white mb-2">
              In Evidenza
            </h2>
            <p className="text-gray-400">
              I locali più amati dal club
            </p>
          </div>
          <Link to="/locali" className="hidden md:block">
            <Button variant="secondary">
              Vedi tutti
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-80 rounded-xl" />
            ))}
          </div>
        ) : localiInEvidenza.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {localiInEvidenza.map((locale) => (
              <LocaleCard key={locale.id} locale={locale} />
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">
            Nessun locale in evidenza al momento
          </p>
        )}

        <div className="md:hidden mt-6 text-center">
          <Link to="/locali">
            <Button variant="secondary" className="w-full">
              Vedi tutti i locali
            </Button>
          </Link>
        </div>
      </section>

      {/* Come funziona (preview) */}
      <section className="bg-night-light py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-white mb-4">
              Come Funziona
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Il bollino "Approved" non si compra: si guadagna sul campo,
              durante le nostre uscite in fuoristrada.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="font-display text-2xl text-gold">1</span>
              </div>
              <h3 className="font-display text-xl text-white mb-2">Scopriamo</h3>
              <p className="text-gray-400 text-sm">
                Durante le uscite del club, esploriamo la campagna marchigiana e
                scopriamo trattorie, agriturismi e ristoranti autentici.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="font-display text-2xl text-gold">2</span>
              </div>
              <h3 className="font-display text-xl text-white mb-2">Testiamo</h3>
              <p className="text-gray-400 text-sm">
                Pranziamo e ceniamo insieme, valutando qualità, genuinità,
                prezzi e accoglienza. Il parcheggio per i nostri mezzi è un plus!
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="font-display text-2xl text-gold">3</span>
              </div>
              <h3 className="font-display text-xl text-white mb-2">Approviamo</h3>
              <p className="text-gray-400 text-sm">
                Se il locale supera la prova, entra nella nostra guida con il
                bollino "Approved" e la nostra storia su di loro.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/come-funziona">
              <Button variant="secondary">
                Scopri di più
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Il Club (preview) */}
      <section className="container mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl text-white mb-4">
              Il Club Conero Garage
            </h2>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Siamo un gruppo di appassionati di fuoristrada delle Marche.
              Dal 2010 organizziamo uscite, raid e raduni nella nostra regione
              e non solo. Il nostro nome viene dal Monte Conero, simbolo della
              nostra terra.
            </p>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Con "Approved" vogliamo condividere le nostre scoperte gastronomiche:
              quei posti dove dopo ore di sterrato ti accolgono con un sorriso e
              un piatto che vale il viaggio.
            </p>
            <Link to="/il-club">
              <Button variant="primary">
                Conosci il club
              </Button>
            </Link>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] bg-night-light rounded-xl overflow-hidden border border-gray-800">
              {/* Placeholder per immagine del club */}
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-night-light to-night">
                <div className="text-center">
                  <div className="w-24 h-24 bg-gold rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-night font-display text-4xl font-bold">CG</span>
                  </div>
                  <p className="text-gold font-display text-xl">CONERO GARAGE</p>
                  <p className="text-gray-500 text-sm">Est. 2010</p>
                </div>
              </div>
            </div>
            {/* Badge decorativo */}
            <div className="absolute -bottom-4 -right-4 bg-gold text-night px-4 py-2 rounded-lg shadow-lg">
              <p className="font-display text-lg">4x4 Club</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
